<script>
import cloneDeep from 'lodash.clonedeep';
import { mapState } from 'vuex';
import { DateTime } from 'luxon';
import isFunction from 'lodash/isFunction';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import flatPickr from 'vue-flatpickr-component';
import ReportModel from '@models/Report';
// import CompanyModel from '@models/Company';
import VipModel from '@models/Vip';
import EntityMultiselect from '@components/EntityMultiselect';
import BlockingContainer from '@components/BlockingContainer';
import editFormChange from '@mixins/editFormChange';
import datePickerUtility from '@mixins/datePickerUtility';

export default {
  components: {
    BlockingContainer,
    EntityMultiselect,
    flatPickr,
    FontAwesomeIcon,
  },
  mixins: [editFormChange, datePickerUtility],
  props: {
    value: {
      type: Object,
      default: () => {},
    },
    // eslint-disable-next-line
    loadedId: {
      default: null,
    },
    allExpensesHaveScan: {
      type: Boolean,
      required: true,
      default: () => false,
    },
  },
  data() {
    return {
      reportLocal: {
        vip_id: null,
        vip: null,
        vip_description: null,
        meeting_point: null,
        meeting_date: null,
        completed: 0,
        abroad: 0,
      },
      vipModel: VipModel,
      statuses: [
        { value: 0, text: 'Draft' },
        { value: 1, text: 'Completed' },
      ],
      abroadStatuses: [
        { value: 0, text: 'Inland' },
        { value: 1, text: 'Abroad' },
      ],
    };
  },
  computed: {
    ...mapState({
      dateConfig: (state) => state.common.dateConfigTime,
      status: (state) => state.common.currentReportStatus,
    }),
  },
  watch: {
    status(val) {
      this.$set(this.reportLocal, 'completed', val);
    },
    'value.only_costs': function (val) {
      this.$set(this.reportLocal, 'only_costs', val);
    },
  },
  mounted() {
    if (this.value) {
      this.reportLocal = cloneDeep(this.value);
      this.$store.dispatch('common/updateCurrentReportStatus', this.reportLocal.completed);
      this.$nextTick(() => {
        this.initChangeWatcher(['reportLocal'], this.validate);
      });
    }
  },
  methods: {
    validate(callback) {
      this.$validator.validateAll('report-form').then((result) => {
        if (result) {
          this.$wait.start('report loading');
          this.submit(callback);
        }
      });
    },
    submit(callback) {
      new ReportModel(this.reportLocal)
        .save()
        .then((response) => {
          this.$emit('submitted', response.report);
          this.$emit('input', cloneDeep(this.reportLocal));
          this.$store.dispatch('common/updateCurrentReportStatus', this.reportLocal.completed);

          if (callback && isFunction(callback)) callback();
          this.$wait.end('report loading');
        })
        .catch(() => this.$wait.end('report loading'));
    },
    formatDate(date) {
      return DateTime.fromSQL(date).toFormat('dd.MM.yyyy - HH:mm');
    },
    checkCompletedStatus(checked) {
      if (checked === 1 && !this.allExpensesHaveScan) {
        this.$toasted.error(this.$t('errors.report_status_completed_expenses_not_uploaded'));
        this.reportLocal.completed = 0;
      }
    },
  },
};
</script>

<template>
  <div>
    <BlockingContainer v-if="$wait.is('report loading')"></BlockingContainer>
    <b-form v-if="!value || (!value.locked && !value.completed)" data-vv-scope="report-form" @submit.prevent="validate('report-form')">
      <b-form-row>
        <b-col sm="6">
          <b-form-group>
            <template v-slot:label class="col-form-legend col-12">
              {{ $t('fields.vip') }}:
              <span class="status-star text-danger">*</span>
              <span class="near-label-info text-secondary" @click.stop>
                <FontAwesomeIcon id="vipPopover" :icon="['fas', 'info-circle']" tabindex="-1" />
                <b-popover target="vipPopover" triggers="focus" placement="rightbottom">
                  {{ $t('infos.vip_description') }}
                </b-popover>
              </span>
            </template>
            <EntityMultiselect
              v-model="reportLocal.vip_id"
              v-validate="reportLocal.vip_description ? '' : 'required'"
              data-vv-name="vip_id"
              :data-vv-as="$t('fields.vip')"
              :initial-value="reportLocal.vip"
              :state="errors.has('vip_id', 'report-form')"
              :placeholder="$t('placeholders.vip')"
              :model="vipModel"
              :disabled="!!reportLocal.vip_description"
            >
            </EntityMultiselect>

            <div v-show="errors.has('vip_id', 'report-form')" class="invalid-feedback d-block m-0">
              {{ errors.first('vip_id', 'report-form') }}
            </div>
          </b-form-group>
        </b-col>
        <b-col sm="6">
          <b-form-group>
            <template v-slot:label class="col-form-legend col-12">
              {{ $t('fields.vip_description') }}:
              <span class="status-star text-danger">*</span>
              <span class="near-label-info text-secondary" @click.stop>
                <FontAwesomeIcon id="customerDescriptionPopover" :icon="['fas', 'info-circle']" tabindex="-1" />
                <b-popover target="customerDescriptionPopover" triggers="focus" placement="rightbottom">
                  {{ $t('infos.vip_description') }}
                </b-popover>
              </span>
            </template>
            <b-form-input
              v-model="reportLocal.vip_description"
              v-validate="reportLocal.vip_id ? 'max:255' : 'required|max:255'"
              data-vv-value-path="localValue"
              data-vv-name="vip_description"
              :data-vv-as="$t('fields.vip_description')"
              type="text"
              :state="errors.has('vip_description', 'report-form') ? false : null"
              aria-describedby="input-feedback-vip_description"
              :placeholder="$t('placeholders.vip_description')"
              :disabled="!!reportLocal.vip_id"
            >
            </b-form-input>

            <b-form-invalid-feedback id="input-feedback-vip_description">
              <template v-show="errors.has('vip_description', 'report-form')">{{ errors.first('vip_description', 'report-form') }}</template>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-form-row>

      <b-form-row>
        <b-col sm="3">
          <b-form-group>
            <template v-slot:label class="col-form-legend col-12">
              {{ $t('fields.city_location') }}:
              <span class="status-star text-success">*</span>
              <span class="near-label-info text-secondary" @click.stop>
                <FontAwesomeIcon id="pointPopover" :icon="['fas', 'info-circle']" tabindex="-1" />
                <b-popover target="pointPopover" triggers="focus" placement="rightbottom">
                  {{ $t('infos.job_location') }}
                </b-popover>
              </span>
            </template>
            <b-form-input
              v-model="reportLocal.meeting_point"
              v-validate="'max:255'"
              data-vv-value-path="localValue"
              data-vv-name="meeting_point"
              :data-vv-as="$t('fields.meeting_point')"
              type="text"
              :state="errors.has('meeting_point', 'report-form') ? false : null"
              aria-describedby="input-feedback-meeting_point"
              :placeholder="$t('placeholders.city_location')"
            >
            </b-form-input>

            <b-form-invalid-feedback id="input-feedback-meeting_point">
              <template v-show="errors.has('meeting_point', 'report-form')">{{ errors.first('meeting_point', 'report-form') }}</template>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
        <b-col sm="3">
          <b-form-group>
            <template v-slot:label class="col-form-legend col-12">
              {{ $t('fields.start_date_and_time') }}:
              <span class="status-star text-success">*</span>
              <span class="near-label-info text-secondary" @click.stop>
                <FontAwesomeIcon id="datePopover" :icon="['fas', 'info-circle']" tabindex="-1" />
                <b-popover target="datePopover" triggers="focus" placement="rightbottom">
                  {{ $t('infos.meeting_date') }}
                </b-popover>
              </span>
            </template>
            <flat-pickr
              v-model="reportLocal.meeting_date"
              v-validate="''"
              data-vv-name="meeting_date"
              :data-vv-as="$t('fields.meeting_date')"
              aria-describedby="input-feedback-meeting_date"
              :config="dateConfig"
              class="form-control"
              :class="{ 'is-invalid': errors.has('meeting_date', 'report-form') }"
              :placeholder="$t('placeholders.date')"
              @on-close="closeCalendar('reportLocal.meeting_date', 'dd.MM.yyyy HH:mm', 'yyyy-MM-dd HH:mm:ss', ...arguments)"
            >
            </flat-pickr>
            <b-form-invalid-feedback id="input-feedback-meeting_date">
              <template v-show="errors.has('meeting_date', 'report-form')">{{ errors.first('meeting_date', 'report-form') }} </template>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
        <b-col sm="3">
          <b-form-group>
            <template v-slot:label class="col-form-legend col-12">
              {{ $t('fields.status') }}:
              <span class="near-label-info text-secondary" @click.stop>
                <FontAwesomeIcon id="statusPopover" :icon="['fas', 'info-circle']" tabindex="-1" />
                <b-popover target="statusPopover" triggers="focus" placement="rightbottom">
                  {{ $t('infos.status') }}
                </b-popover>
              </span>
            </template>
            <b-form-radio-group
              v-model="reportLocal.completed"
              buttons
              button-variant="outline-secondary border-gray"
              class="w-100"
              :options="statuses"
              @change="checkCompletedStatus"
            >
            </b-form-radio-group>
          </b-form-group>
        </b-col>
        <b-col v-if="!reportLocal.only_costs" sm="3">
          <b-form-group>
            <template v-slot:label class="col-form-legend col-12">
              {{ $t('fields.area') }}:
              <span class="near-label-info text-secondary" @click.stop>
                <FontAwesomeIcon id="areaPopover" :icon="['fas', 'info-circle']" tabindex="-1" />
                <b-popover target="areaPopover" triggers="focus" placement="rightbottom">
                  {{ $t('infos.area') }}
                </b-popover>
              </span>
            </template>
            <b-form-radio-group v-model="reportLocal.abroad" buttons button-variant="outline-secondary border-gray" class="w-100" :options="abroadStatuses">
            </b-form-radio-group>
          </b-form-group>
        </b-col>
      </b-form-row>

      <b-button type="submit" :disabled="errors.any('report-form')" :variant="'outline-' + (!errors.any('report-form') ? 'dark' : 'danger')">
        {{ $t('actions.submit') }}
      </b-button>
    </b-form>
    <div v-else>
      <div class="mb-2 d-flex">
        <strong class="report-label">{{ $t('fields.vip') }}:</strong>
        <span>{{ reportLocal.vip ? reportLocal.vip.name : reportLocal.vip_description }}</span>
      </div>
      <div class="mb-2 d-flex">
        <strong class="report-label">
          {{ $t('fields.city_location') }}:
        </strong>
        <span>{{ reportLocal.meeting_point }}</span>
      </div>
      <div class="mb-2 d-flex">
        <strong class="report-label">
          {{ $t('fields.start_date_and_time') }}:
        </strong>
        <span>{{ formatDate(reportLocal.meeting_date) }}</span>
      </div>
    </div>
  </div>
</template>
<style scoped>
.report-label {
  flex: 0 0 180px;
}

/deep/ .btn-group {
  display: flex;
}

/deep/ .btn-group .btn {
  flex: 1 1 50%;
  max-width: 50%;
}
</style>